import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useOnboardingState, useRecordFirstLoginRedirect } from '@/hooks/use-onboarding';
import { useAuth } from '@/contexts/auth-context';
import { AlertCircle, X } from 'lucide-react';
import { Button } from '@/app/components/ui/button';

interface OnboardingRedirectProps {
  children: React.ReactNode;
}

// Pages the first-login redirect must never interrupt: account security
// steps (MFA enrollment, forced password change), logging out, and the
// wizard itself.
const NO_REDIRECT_PATHS = ['/enroll-mfa', '/change-password', '/logout', '/onboarding'];

function firstLoginRedirectStorageKey(userId: number, organizationId: number | null): string {
  return `h3_onboarding_first_login_redirect_${userId}_${organizationId}`;
}

function readRedirectFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeRedirectFlag(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    // Storage unavailable (private mode etc.) — the backend flag still
    // records the redirect.
  }
}

export function OnboardingRedirect({ children }: OnboardingRedirectProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading } = useOnboardingState();
  const recordRedirect = useRecordFirstLoginRedirect();
  const [promptDismissed, setPromptDismissed] = useState(false);
  // Key of the user+org the redirect last fired for, so switching to another
  // org in the same session can still redirect once for that org.
  const redirectedKey = useRef<string | null>(null);

  // First login: send an org admin with an unfinished setup straight to the
  // wizard, exactly once per org (the backend flag is the source of truth;
  // the localStorage flag only guards against a repeat before the refetched
  // state arrives).  Completed steps don't matter here — steps auto-complete
  // from real records, so a brand-new org already has some done (e.g. the
  // admin counts as an active tech).  The onboarding state only loads once the
  // user is fully signed in — MFA enrollment and forced password changes are
  // enforced by the backend before any API call succeeds — so a loaded
  // state means those steps are done.
  const isRedirectPath = NO_REDIRECT_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );
  const shouldRedirect =
    !!user &&
    !user.is_staff &&
    !!data &&
    data.is_admin &&
    !data.onboarding_completed &&
    !data.first_login_redirect_done &&
    !isRedirectPath &&
    !readRedirectFlag(firstLoginRedirectStorageKey(user.id, user.organization));

  useEffect(() => {
    if (!shouldRedirect || !user) return;
    const key = firstLoginRedirectStorageKey(user.id, user.organization);
    if (redirectedKey.current === key) return;
    redirectedKey.current = key;
    writeRedirectFlag(key);
    // If the backend cannot record it, the hook clears the local flag so a
    // later visit retries rather than leaving the org unmarked.
    recordRedirect.mutate({ storageKey: key });
    navigate('/onboarding');
  }, [shouldRedirect, user, recordRedirect, navigate]);

  // Platform admin users (is_staff with no tenant org) should never see the
  // onboarding wizard — they operate at the platform level, not org level.
  if (user?.is_staff && !user.organization) {
    return <>{children}</>;
  }

  // While the onboarding state is loading, render children normally so the
  // page doesn't flash a blank screen.  The query is fast (single GET) and
  // is cached by React Query, so the redirect will fire almost immediately
  // on subsequent navigations.
  if (isLoading || !data) {
    return <>{children}</>;
  }

  const showPrompt =
    data.is_admin &&
    !data.onboarding_completed &&
    !location.pathname.startsWith('/onboarding') &&
    location.pathname !== '/dashboard' &&
    location.pathname !== '/my-dashboard' &&
    !promptDismissed;

  return (
    <>
      {showPrompt && (
        <div className="mx-4 mt-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 sm:mx-6 lg:mx-8">
          <AlertCircle className="h-5 w-5 shrink-0 text-amber-600" />
          <p className="min-w-0 flex-1 text-sm">
            Setup is still in progress ({data.completed_count} of {data.total_count} done).
          </p>
          <Button asChild size="sm" variant="outline">
            <Link to="/onboarding">Resume Setup</Link>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Dismiss setup prompt"
            onClick={() => setPromptDismissed(true)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      {children}
    </>
  );
}
